/**
 * Confidence colour-coding for extracted steps/segments (admin debug views).
 *
 * Bands: ≥ 0.9 green (high), ≥ 0.75 yellow (mid), < 0.75 red (low).
 * Low/mid extraction confidence is the signal that the analyze step may be
 * judging a mis-read transcription — see
 * docs/FeatureDesigns/PipelineInfra/provenance-aware-escalation.md.
 */

export const CONFIDENCE_HIGH = 0.9;
export const CONFIDENCE_MID = 0.75;

export type ConfidenceBand = 'high' | 'mid' | 'low';

export function confidenceBand(value: number | undefined): ConfidenceBand | undefined {
    if (typeof value !== 'number') return undefined;
    if (value >= CONFIDENCE_HIGH) return 'high';
    if (value >= CONFIDENCE_MID) return 'mid';
    return 'low';
}

/** Class suffix for the band, e.g. 'conf-high' | 'conf-mid' | 'conf-low'. */
export function confidenceClass(value: number | undefined): string {
    const band = confidenceBand(value);
    return band ? `conf-${band}` : '';
}

/** Inline confidence pill, colour-coded by band. Renders nothing when the
 *  value isn't a number. */
export function ConfidenceBadge({ value, label = 'confidence' }: { value: number | undefined; label?: string }) {
    if (typeof value !== 'number') return null;
    return (
        <span className={`confidence-badge ${confidenceClass(value)}`}>
            {label}: {value.toFixed(2)}
        </span>
    );
}
